/**
 * Component: Video Element
 * -----------------------------------------------------------------------------
 * Logic related to video elements.
 * @namespace videoElement
 */

import { on } from '~/helpers/utils'

/**
 * DOM selectors.
 */
const selectors = {
  videoWrapper: '.video-autoplay-wrapper',
}

/**
 * Create a new video element object.
 */
export default () => {

  /**
   * Check if an element is in the viewport.
   * @param {Element} wrapper - Video wrapper element.
   * @returns {Boolean}
   */
  function isInViewport(wrapper) {
    const rect = wrapper.getBoundingClientRect()

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  /**
   * Set video src, load and play video.
   * @param {Element} wrapper - Video wrapper element.
   */
  function setVideoSrc(wrapper) {
    const video = wrapper.querySelector('video')
    const source = video.querySelector('source')

    if (source.getAttribute('src') === video.dataset.src) {
      return
    }

    source.setAttribute('src', video.dataset.src)
    video.classList.add('is-active')
    video.load()
  }

  /**
   * Set event listeners.
   * @param {Array} videoWrapper - Array of video wrappers.
   */
  function setEventListeners(videoWrapper) {
    videoWrapper.forEach((wrapper) => {
      if (isInViewport(wrapper)) {
        setVideoSrc(wrapper)

      } else {
        on('scroll', document, () => {
          if (isInViewport(wrapper)) {
            setVideoSrc(wrapper)
          }
        })
      }
    })
  }

  /**
   * Initialize component.
   */
  function init() {
    const videoWrapper = [...document.querySelectorAll(selectors.videoWrapper)]

    if (!videoWrapper.length) {
      return
    }

    setEventListeners(videoWrapper)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
